/**
 * Demo-team entry / exit for the shared read-only example workspace.
 *
 * Entering the demo is a client-side "view as": we point the active team at the
 * shared demo workspace WITHOUT changing the user's server-side default team, so
 * a page refresh or next login still lands them on their real team. We stash the
 * team they came from so "Exit demo" can restore it exactly.
 *
 * The demo team is read-only everywhere except chat; that is enforced
 * authoritatively on the backend (blockDemoMutations). The frontend just hides
 * mutating affordances — see DemoReadOnly context / banner.
 */
import axiosInstance from 'shared/services/api/axiosInstance';

const RETURN_KEY = 'trodo_pre_demo_team';

/** Is the currently-active team the demo team? */
export function isDemoTeamActive() {
  try {
    const raw = localStorage.getItem('selectedTeamData');
    if (!raw) return false;
    const t = JSON.parse(raw);
    return t?.isDemo === true || t?.is_demo === true;
  } catch {
    return false;
  }
}

function persistActiveTeam(teamData, setSelectedTeamContext) {
  localStorage.setItem('selectedTeam', teamData.name);
  localStorage.setItem('selectedTeamData', JSON.stringify(teamData));
  if (setSelectedTeamContext) setSelectedTeamContext(teamData);
  window.dispatchEvent(
    new CustomEvent('teamDataUpdated', { detail: { teamData } }),
  );
}

/**
 * Switch the user into the shared demo team and drop them on its chat page.
 * Idempotent-ish: if already in demo, this just re-navigates.
 */
export async function enterDemoTeam(navigate, setSelectedTeamContext) {
  const { data } = await axiosInstance.get('/team/demo');
  if (!data || (data.id == null && data._id == null)) {
    throw new Error('Demo workspace is unavailable right now.');
  }

  // Stash the team we're leaving so Exit can restore it — but never stash the
  // demo team itself (guards against double-enter losing the real team).
  if (!isDemoTeamActive()) {
    const current = localStorage.getItem('selectedTeamData');
    if (current) sessionStorage.setItem(RETURN_KEY, current);
  }

  const demoData = {
    ...data,
    isDemo: true,
    // A demo view is never gated by onboarding.
    activated: true,
    _lastFetched: Date.now(),
  };
  persistActiveTeam(demoData, setSelectedTeamContext);
  navigate(`/${encodeURIComponent(demoData.name)}/chat`);
}

/**
 * Leave the demo team and restore whatever team the user came from. Falls back
 * to /dashboard (which resolves their default team) if we have nothing stashed.
 */
export function exitDemoTeam(navigate, setSelectedTeamContext) {
  const stashed = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  let team = null;
  try {
    team = stashed ? JSON.parse(stashed) : null;
  } catch {
    team = null;
  }

  if (!team || !team.name) {
    navigate('/dashboard');
    return;
  }
  persistActiveTeam({ ...team, _lastFetched: Date.now() }, setSelectedTeamContext);
  navigate(`/${encodeURIComponent(team.name)}/chat`);
}
